import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import type { PermissionCode } from "@rbim/shared";
import { useAuth } from "./auth";
import { PortalLayout } from "./components/portal-layout";
import { AccessDeniedPage } from "./pages/access-denied-page";
import { AddNewDataPage } from "./pages/add-new-data-page";
import { AuditPage } from "./pages/audit-page";
import { BackupStatusPage } from "./pages/backup-status-page";
import { DashboardPage } from "./pages/dashboard-page";
import { DashboardPreviewPage } from "./pages/dashboard-preview-page";
import { EditRbimRecordPage } from "./pages/edit-rbim-record-page";
import { LoginPage } from "./pages/login-page";
import { SynchronizationPage } from "./pages/synchronization-page";
import { SystemAdministrationPage } from "./pages/system-administration-page";
import { UserManagementPage } from "./pages/user-management-page";
import { AboutRbimPage } from "./pages/about-rbim-page";
import { CertificatesPage } from "./pages/certificates-page";
import { DataManagementPage } from "./pages/data-management-page";
import { HouseholdProfilesPage } from "./pages/household-profiles-page";
import { IndividualProfilesPage } from "./pages/individual-profiles-page";
import { LiraAssistantPage } from "./pages/lira-assistant-page";
import { ReportAgeNamesPage } from "./pages/report-age-names-page";
import { ReportDetailPage } from "./pages/report-detail-page";
import { ReportsPage } from "./pages/reports-page";
import { RecordReviewPage } from "./pages/record-review-page";
import { SettingsPage } from "./pages/settings-page";

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {import.meta.env.DEV && <Route path="/dashboard-preview" element={<DashboardPreviewPage />} />}
      <Route element={<ProtectedLayout />}>
        <Route index element={<HomeRoute />} />
        <Route
          path="add-new-data"
          element={
            <PermissionRoute permission="record.create">
              <AddNewDataPage />
            </PermissionRoute>
          }
        />
        <Route path="records/:id/edit" element={<PermissionRoute permission="record.update"><EditRbimRecordPage /></PermissionRoute>} />
        <Route path="households" element={<PermissionRoute permission="record.view"><HouseholdProfilesRoute /></PermissionRoute>} />
        <Route path="individuals" element={<PermissionRoute permission="record.view"><IndividualProfilesRoute /></PermissionRoute>} />
        <Route path="record-review" element={<PermissionRoute permission="record.review"><RecordReviewPage /></PermissionRoute>} />
        <Route path="certificates" element={<PermissionRoute permission="certificate.view"><CertificatesPage /></PermissionRoute>} />
        <Route path="reports" element={<PermissionRoute permission="report.view"><ReportsPage /></PermissionRoute>} />
        <Route path="reports/:reportId" element={<PermissionRoute permission="report.view"><ReportDetailPage /></PermissionRoute>} />
        <Route path="reports/:reportId/names" element={<MunicipalReportNamesRoute />} />
        <Route path="ai-assistant" element={<AiAssistantRoute />} />
        <Route path="data-management" element={<AnyPermissionRoute permissions={["data.import", "data.export"]}><DataManagementPage /></AnyPermissionRoute>} />
        <Route path="settings" element={<PermissionRoute permission="settings.manage"><SettingsPage /></PermissionRoute>} />
        <Route path="about" element={<PermissionRoute permission="dashboard.view"><AboutRbimPage /></PermissionRoute>} />
        <Route path="users" element={<PermissionRoute permission="account.manage"><UserManagementPage /></PermissionRoute>} />
        <Route path="audit" element={<PermissionRoute permission="audit.view"><AuditPage /></PermissionRoute>} />
        <Route path="synchronization" element={<PermissionRoute permission="sync.monitor"><SynchronizationPage /></PermissionRoute>} />
        <Route path="backup-status" element={<PermissionRoute permission="backup.monitor"><BackupStatusPage /></PermissionRoute>} />
        <Route path="access-denied" element={<AccessDeniedPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function HomeRoute() {
  const { user } = useAuth();
  if (user?.permissions.includes("system.monitor")) return <SystemAdministrationPage />;
  if (user?.permissions.includes("dashboard.view")) return <DashboardPage />;
  return <Navigate to="/access-denied" replace />;
}

function ProtectedLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <main className="centered-state">Checking your secure session…</main>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <PortalLayout><Outlet /></PortalLayout>;
}

function PermissionRoute({ permission, children }: { permission: PermissionCode; children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user?.permissions.includes(permission)) return <Navigate to="/access-denied" replace />;
  return children;
}

function AiAssistantRoute() {
  const { user } = useAuth();
  if (
    !user ||
    user.role === "BARANGAY_ENCODER" ||
    user.role === "MAYOR_VIEWER" ||
    !user.permissions.includes("ai.query")
  ) {
    return <Navigate to="/access-denied" replace />;
  }
  return <LiraAssistantPage />;
}

function AnyPermissionRoute({
  permissions,
  children,
}: {
  permissions: readonly PermissionCode[];
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  if (!user || !permissions.some((permission) => user.permissions.includes(permission))) {
    return <Navigate to="/access-denied" replace />;
  }
  return children;
}

function HouseholdProfilesRoute() {
  const { user } = useAuth();
  return <HouseholdProfilesPage
    enableUpdateActions={user?.permissions.includes("record.update") === true}
    enableFullRecordEdit={user?.permissions.includes("record.update") === true}
    enableDeleteActions={user?.role === "MUNICIPAL_CITY_ADMINISTRATOR" && user?.permissions.includes("record.delete") === true}
    enableSupervisorOtpDeleteActions={user?.role === "BARANGAY_SUPERVISOR"}
  />;
}

function IndividualProfilesRoute() {
  const { user } = useAuth();
  return <IndividualProfilesPage
    enableUpdateActions={user?.permissions.includes("record.update") === true}
    enableSupervisorOtpDeleteActions={user?.role === "BARANGAY_SUPERVISOR"}
  />;
}


function MunicipalReportNamesRoute() {
  const { user } = useAuth();
  if (
    !user ||
    !user.permissions.includes("report.view") ||
    (user.role !== "MUNICIPAL_CITY_USER" && user.role !== "MUNICIPAL_CITY_ADMINISTRATOR")
  ) {
    return <Navigate to="/access-denied" replace />;
  }
  return <ReportAgeNamesPage />;
}
